import { ref, type Ref } from "vue"
import './useDrag.css'

const DRAG_CLASS = 'use-drag-xy'

export function useDrag<From, To = From>(onDrop: (from: From, to: To) => void) {
    const dragging: Ref<From | undefined> = ref()
    const over: Ref<To | undefined> = ref()
    const dragPosition: Ref<{ x: number, y: number } | undefined> = ref()
    const draggedEl: Ref<HTMLElement | null> = ref(null)

    // Track active pointer for touch drag
    let activePointerId: number | null = null
    let currentItem: From | undefined = undefined

    // Map to store registered targets by key (for pointer-based hit testing)
    const targetMap = new Map<string, To>()

    // Shared cleanup function
    function cleanup() {
        if (draggedEl.value) {
            draggedEl.value.classList.remove(DRAG_CLASS)
            draggedEl.value.style.removeProperty('--use-drag-x')
            draggedEl.value.style.removeProperty('--use-drag-y')
            draggedEl.value.style.removeProperty('--use-drag-w')
            draggedEl.value.style.removeProperty('--use-drag-h')
            draggedEl.value.style.removeProperty('--use-drag-offset-x')
            draggedEl.value.style.removeProperty('--use-drag-offset-y')
        }
        dragging.value = undefined
        over.value = undefined
        dragPosition.value = undefined
        draggedEl.value = null
        activePointerId = null
        currentItem = undefined
    }

    // Shared start drag function
    function startDrag(el: HTMLElement, clientX: number, clientY: number, item: From) {
        dragging.value = item
        currentItem = item
        dragPosition.value = { x: clientX, y: clientY }
        draggedEl.value = el

        // Capture size and click offset before position: fixed changes it
        const rect = el.getBoundingClientRect()
        el.style.setProperty('--use-drag-w', rect.width + 'px')
        el.style.setProperty('--use-drag-h', rect.height + 'px')
        // Offset as percentage (where in element was clicked)
        const offsetX = ((clientX - rect.left) / rect.width) * 100
        const offsetY = ((clientY - rect.top) / rect.height) * 100
        el.style.setProperty('--use-drag-offset-x', offsetX + '%')
        el.style.setProperty('--use-drag-offset-y', offsetY + '%')

        // Set initial position
        el.style.setProperty('--use-drag-x', clientX + 'px')
        el.style.setProperty('--use-drag-y', clientY + 'px')
    }

    // Shared move function
    function moveDrag(clientX: number, clientY: number) {
        if (draggedEl.value && !draggedEl.value.classList.contains(DRAG_CLASS)) {
            draggedEl.value.classList.add(DRAG_CLASS)
        }
        if (clientX || clientY) {
            dragPosition.value = { x: clientX, y: clientY }
            if (draggedEl.value) {
                draggedEl.value.style.setProperty('--use-drag-x', clientX + 'px')
                draggedEl.value.style.setProperty('--use-drag-y', clientY + 'px')
            }
        }
    }

    // Find target at a point (for touch drag)
    function findTargetAt(x: number, y: number): To | undefined {
        // Use elementsFromPoint to find all elements at the point, including those behind the dragged element
        const elements = document.elementsFromPoint(x, y)
        for (const el of elements) {
            // Skip the dragged element itself
            if (el === draggedEl.value || draggedEl.value?.contains(el)) continue

            const targetEl = el.closest('[data-drag-target]') as HTMLElement | null
            if (targetEl) {
                const key = targetEl.getAttribute('data-drag-target')
                if (key) {
                    return targetMap.get(key)
                }
            }
        }
        return undefined
    }

    // Global pointer move/up handlers for touch drag
    function onPointerMove(e: PointerEvent) {
        if (e.pointerId !== activePointerId) return
        e.preventDefault()
        moveDrag(e.clientX, e.clientY)

        const target = findTargetAt(e.clientX, e.clientY)
        if (target !== undefined) {
            over.value = target
        }
    }

    function onPointerUp(e: PointerEvent) {
        if (e.pointerId !== activePointerId) return

        const target = findTargetAt(e.clientX, e.clientY)
        if (target !== undefined && dragging.value !== undefined) {
            onDrop(dragging.value, target)
        }

        cleanup()
        document.removeEventListener('pointermove', onPointerMove)
        document.removeEventListener('pointerup', onPointerUp)
        document.removeEventListener('pointercancel', onPointerUp)
    }

    return {
        dragging,
        over,
        dragPosition,
        draggedEl,

        source: (item: From) => ({
            // HTML5 Drag API for desktop
            onDragstart(e: DragEvent) {
                const el = e.currentTarget as HTMLElement
                startDrag(el, e.clientX, e.clientY, item)

                // Hide native drag image
                const empty = document.createElement('div')
                empty.style.opacity = '0'
                document.body.appendChild(empty)
                e.dataTransfer?.setDragImage(empty, 0, 0)
                requestAnimationFrame(() => empty.remove())
            },
            onDragend() {
                cleanup()
            },
            onDrag(e: DragEvent) {
                moveDrag(e.clientX, e.clientY)
            },

            // Pointer events for touch (immediate, no long-press needed)
            onPointerdown(e: PointerEvent) {
                // Only handle touch, let mouse use native drag API
                if (e.pointerType !== 'touch') return

                e.preventDefault()
                activePointerId = e.pointerId
                const el = e.currentTarget as HTMLElement
                el.setPointerCapture(e.pointerId)
                startDrag(el, e.clientX, e.clientY, item)

                document.addEventListener('pointermove', onPointerMove)
                document.addEventListener('pointerup', onPointerUp)
                document.addEventListener('pointercancel', onPointerUp)
            },
        }),
        target: (item: To, key?: string) => ({
            'data-drag-target': key ?? '',
            onDragover(e: DragEvent) {
                e.preventDefault()
                over.value = item
            },
            onDrop() {
                if (dragging.value !== undefined) {
                    onDrop(dragging.value, item)
                    dragging.value = undefined
                }
                over.value = undefined
            }
        }),

        // Register a target by key for pointer-based hit testing
        registerTarget(key: string, item: To) {
            targetMap.set(key, item)
        },
        unregisterTarget(key: string) {
            targetMap.delete(key)
        },
    }
}
